<script setup lang="ts">
import type { MailItem } from '@/api/mail/type'
import { useUserStore } from '@/stores/modules/user'
import type { ReceiveMailRes } from '@/types/mail'
import { socketEmitAsync } from '@/utils/web-socket'
import { ElMessage, dayjs } from 'element-plus'
import { useCommonStore } from '@/stores/modules/common'
import { ref } from 'vue'
import { getMailDetailApi } from '@/api/mail'
import type { DailyChallengeModeType } from '@/types/daily-challenge'
import type { ConsumablesModeType } from '@/types/consumables'

const commonStore = useCommonStore()
const userStore = useUserStore()
const props = defineProps<{ id: number }>()
const emits = defineEmits(['success', 'showReceive'])

const mail = ref<MailItem>()
async function getMailInfo() {
  const res = await getMailDetailApi({ id: props.id })
  mail.value = res
}
getMailInfo()

async function handleReceive() {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('装备背包已满')
  if (userStore.runeList.length >= userStore.getRuneCapacity) return ElMessage.warning('符石背包已满')
  if (userStore.petList.length >= userStore.getPetCapacity) return ElMessage.warning('魔物牧场已满')
  if (userStore.gold > 2000000000) return ElMessage.warning('金币已达上限，无法领取邮件')
  const res = await socketEmitAsync<ReceiveMailRes>({ type: 'receiveMail', data: { idList: [props.id] } })
  const { itemList, gold, vipLevel, diamond, crystal, duelCrystal, consumables, petList, runeList, relicList } = res
  if (itemList) userStore.itemList.push(...itemList)
  userStore.gold += gold
  userStore.diamond += diamond
  userStore.crystal += crystal
  userStore.duelCrystal += duelCrystal
  if (vipLevel > userStore.vipLevel) {
    const growTimes = vipLevel + 1
    let key: DailyChallengeModeType
    for (key in userStore.dailyChallengeTimes) {
      userStore.dailyChallengeTimes[key] += growTimes
    }
    // 魔物巢穴次数增加
    if (userStore.vipLevel === 0 && vipLevel === 3) {
      userStore.nestTimes += 2
    } else if (!(userStore.vipLevel === 1 && vipLevel === 2)) {
      userStore.nestTimes++
    }
    // 符石遗迹次数增加
    if (userStore.soulType && userStore.vipLevel === 0) {
      userStore.runeRuinTimes++
    }

    userStore.vipLevel = vipLevel
  }
  if (consumables) {
    let key: ConsumablesModeType
    for (key in consumables) {
      userStore.consumables[key] += consumables[key]!
    }
  }
  if (petList) {
    userStore.petList.push(...petList)
  }
  if (runeList) {
    userStore.runeList.push(...runeList)
  }
  if (relicList?.length) {
    relicList.forEach((item) => {
      const relicInfo = userStore.relicList.find((relic) => item.relicId === relic.relicId)
      if (relicInfo) {
        relicInfo.count++
      } else {
        userStore.relicList.push(item)
      }
    })
  }
  ElMessage.success('领取成功')
  emits('showReceive', mail.value, true)
}
</script>

<template>
  <div class="mail-detail" v-loading="commonStore.loading">
    <template v-if="mail">
      <h2>{{ mail.title }}</h2>
      <p class="sub-title">
        <span>By: {{ mail.from }}</span>
        <span>{{ dayjs(mail.created).format('YYYY-MM-DD HH:mm:ss') }}</span>
      </p>
      <p class="content">{{ mail.content }}</p>
      <CommonBtn type="success" @click.stop="handleReceive" v-show="!mail.isGet" v-loading="commonStore.loading">领取奖励</CommonBtn>
      <CommonBtn type="success" disabled v-show="mail.isGet && !mail.itemList?.length">已领取</CommonBtn>
      <CommonBtn @click.stop="emits('showReceive', mail)" v-show="mail.isGet && mail.itemList?.length">查看领取</CommonBtn>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.mail-detail {
  min-height: 100px;
  .sub-title {
    display: flex;
    justify-content: space-between;
  }
  .content {
    font-size: 16px;
  }
}
</style>
